import React from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { useTranslate } from '../TranslationText';

/**
 * Clean date input with React Hook Form integration.
 * Uses native HTML date input (no MUI DatePicker dependency).
 *
 * @param {string} name - Form field name
 * @param {string} label - Label text
 * @param {boolean} required - Show required asterisk
 * @param {boolean} disabled - Disable input
 * @param {object} rules - Validation rules
 * @param {string} minDate - Minimum selectable date (YYYY-MM-DD)
 * @param {string} maxDate - Maximum selectable date (YYYY-MM-DD)
 * @param {boolean} hideLabel - Hide the label
 */
export default function FormDateInput({
    name,
    label,
    required = false,
    disabled = false,
    rules,
    minDate,
    maxDate,
    hideLabel = false,
    noMargin = false,
    hideError = false,
}) {
    const { control, formState: { errors } } = useFormContext();
    const { translate } = useTranslate();

    const validationRules = {
        ...(required && {
            required: translate('web.requiredField', 'Required Field'),
        }),
        ...rules,
    };

    const fieldError = name.split('.').reduce((obj, key) => obj?.[key], errors);

    return (
        <div className={noMargin ? '' : 'mb-2'}>
            {!hideLabel && label && (
                <label htmlFor={name} className="capatalize_text">
                    {label}{' '}
                    {required && <i style={{ color: 'red', margin: 'auto 2px' }}>*</i>}
                </label>
            )}

            <Controller
                name={name}
                control={control}
                rules={validationRules}
                render={({ field }) => (
                    <input
                        type="date"
                        id={name}
                        ref={field.ref}
                        className={`form-control rounded rtl${fieldError ? ' is-invalid' : ''}`}
                        value={field.value || ''}
                        onChange={(e) => field.onChange(e.target.value)}
                        onBlur={field.onBlur}
                        disabled={disabled}
                        min={minDate}
                        max={maxDate}
                    />
                )}
            />

            {!hideError && fieldError && (
                <div className="text-danger" style={{ fontSize: 12, marginTop: 4 }}>
                    {fieldError.message || translate('web.requiredField', 'Required Field')}
                </div>
            )}
        </div>
    );
}
